import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import '../../styles/Services.css';
import Header from '../../components/layout/Header';
import { MdVerified } from 'react-icons/md';
import { FaStar } from 'react-icons/fa';
import { TiDeleteOutline } from 'react-icons/ti';
import type { IconBaseProps, IconType } from 'react-icons';
import { Accordion, Container } from 'react-bootstrap';
import type { CurrencyCode } from '../../config/pricing';
import { useCurrency } from '../../context/CurrencyContext';
import { getServicePriceSnapshot } from '../../utils/servicePricing';
import { trackMatomoEvent } from '../../utils/matomo';
import { getServicePlans, type ServicePlan } from '../../data/servicePlans';

const asIconComponent = (Icon: IconType, name: string) => {
  const Comp: React.FC<IconBaseProps> = (props) => {
    const element = Icon(props);
    return React.isValidElement(element) ? element : null;
  };
  Comp.displayName = name;
  return Comp;
};

const StarIcon = asIconComponent(FaStar, 'StarIcon');
const VerifiedIcon = asIconComponent(MdVerified, 'VerifiedIcon');
const TiDelete = asIconComponent(TiDeleteOutline, 'TiDeleteOutline');

interface PricedPlan extends ServicePlan {
  pricing: ReturnType<typeof getServicePriceSnapshot>;
}

const Services = () => {
  const { i18n, t } = useTranslation();
  const { currency, setCurrency, supportedCurrencies } = useCurrency();
  const specialistRequestHref =
    process.env.REACT_APP_SPECIALIST_REQUEST_URL ??
    'https://bluealps.yayforms.link/ak5YjNg';
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const currencyLabels: Record<CurrencyCode, string> = {
    CHF: t('services.currency_selector_options.CHF'),
    USD: t('services.currency_selector_options.USD'),
    EUR: t('services.currency_selector_options.EUR'),
  };

  const plans = getServicePlans(t);

  const pricedPlans: PricedPlan[] = plans.map((plan) => ({
    ...plan,
    pricing: getServicePriceSnapshot(plan.key, {
      currency,
      locale,
    }),
  }));

  return (
    <>
      <Header />
      {/* as="main" gives the page the single <main> landmark screen readers
          navigate by; it renders the same element box as before. */}
      <Container as="main" className="services-bg">
        <div className="section-container">
          <div>
            <section className="text-center mt-10">
              <h1>{t('services.hero_title')}</h1>
              <p className="mt-4">{t('services.hero_subtitle')}</p>
              <p className="services-direct-contact-note">
                {t('services.direct_contact_note')}
              </p>
            </section>

            <section
              className="services-currency-section"
              aria-labelledby="services-currency-title"
            >
              <div className="services-currency-copy">
                <span className="services-currency-kicker">
                  {t('services.currency_selector_kicker')}
                </span>
                <h2 id="services-currency-title">
                  {t('services.currency_selector_title')}
                </h2>
                <p>{t('services.currency_selector_subtitle')}</p>
              </div>

              <div
                className="services-currency-options"
                role="radiogroup"
                aria-label={t('services.currency_selector_aria_label')}
              >
                {supportedCurrencies.map((option) => {
                  const isActive = option === currency;

                  return (
                    <button
                      key={option}
                      type="button"
                      className={`services-currency-chip${isActive ? ' is-active' : ''}`}
                      onClick={() => {
                        trackMatomoEvent(
                          'Preference',
                          'currency_change',
                          option,
                        );
                        setCurrency(option);
                      }}
                      role="radio"
                      aria-checked={isActive}
                    >
                      <span className="services-currency-chip-code">
                        {option}
                      </span>
                      <span className="services-currency-chip-name">
                        {currencyLabels[option]}
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>
          </div>

          <section className="py-5 bg-deep">
            <div className="section-container">
              <div className="row row-cols-1 row-cols-md-2 row-cols-lg-4 g-4">
                {pricedPlans.map((plan) => {
                  const isSpecialistRequest = plan.key !== 'audit';

                  return (
                    <div className="col" key={plan.key}>
                      <div
                        className={`pricing-card pricing-card--${plan.key}${plan.recommended ? ' recommended-card' : ''}`}
                      >
                        <div className="pricing-card-body">
                          {plan.recommended && plan.recommendedLabel && (
                            <span className="recommended-badge">
                              {plan.recommendedLabel}
                            </span>
                          )}

                          {plan.pricing.hasConfiguredPrice && (
                            <div className="price-floating-chip">
                              <p className="price-floating-label">
                                {t('services.plan_price_from')}
                              </p>
                              <p className="price-floating-value">
                                {plan.pricing.formattedPrice}
                              </p>
                            </div>
                          )}

                          <div className="plan-header">
                            <span className="plan-badge" aria-hidden>
                              <StarIcon />
                            </span>
                            <div className="plan-title-stack">
                              <h3 className="h5 mb-0 plan-title">
                                {plan.title}
                              </h3>
                              {plan.titleNote && (
                                <p className="plan-title-note">
                                  {plan.titleNote}
                                </p>
                              )}
                            </div>
                          </div>

                          <div className="price-contant">
                            <p className="card-desc mb-3">{plan.desc}</p>
                          </div>

                          <ul className="list-unstyled text-start flex-grow-1 feature-list">
                            {plan.features.map((feature, idx) => (
                              <li key={idx}>
                                <span
                                  className={
                                    feature.type === 'included'
                                      ? 'feature-icon'
                                      : 'feature-icon-no'
                                  }
                                  aria-hidden
                                >
                                  {feature.type === 'included' ? (
                                    <VerifiedIcon />
                                  ) : (
                                    <TiDelete />
                                  )}
                                </span>
                                <span
                                  className={
                                    feature.type === 'included'
                                      ? 'topics'
                                      : 'label-focus'
                                  }
                                >
                                  {feature.text}
                                </span>
                              </li>
                            ))}
                          </ul>

                          <div className="pricing-cta">
                            <a
                              href={
                                isSpecialistRequest
                                  ? specialistRequestHref
                                  : '/contact'
                              }
                              className={plan.ctaClassName}
                              onClick={() =>
                                trackMatomoEvent(
                                  'CTA',
                                  'click',
                                  `Service ${plan.key}`,
                                )
                              }
                              target={
                                isSpecialistRequest ? '_blank' : undefined
                              }
                              rel={
                                isSpecialistRequest
                                  ? 'noopener noreferrer'
                                  : undefined
                              }
                            >
                              {plan.ctaLabel}
                            </a>
                            <Link
                              to={`/services/${plan.key}`}
                              className="btn-lg btn-details"
                              onClick={() =>
                                trackMatomoEvent(
                                  'Navigation',
                                  'click',
                                  `Service details ${plan.key}`,
                                )
                              }
                            >
                              {t('services.view_details')}
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="services-long-term-cta">
                <p className="services-long-term-cta__note">
                  {t('services.long_term_note')}
                </p>
                <a
                  href="/contact"
                  className="btn-lg btn-gradient"
                  onClick={() =>
                    trackMatomoEvent('CTA', 'click', 'Long-term services')
                  }
                >
                  {t('services.long_term_cta')}
                </a>
              </div>
            </div>
          </section>
          {/*    <section className="py-5 bg-deep">
            <div className="section-container">
              <section className="text-center faq-title">
                <h1>{t('services.commercial_table_title')}</h1>
              </section>

              <div className="maintenance-table-wrapper">
                <table className="maintenance-table commercial-table">
                  <thead>
                    <tr>
                      <th>{t('services.commercial_table.billing')}</th>
                      <th>{t('services.commercial_table.hourly_rate')}</th>
                      <th className="commercial-table__commitment">
                        {t('services.commercial_table.commitment')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {commercialTableRows.map((row) => (
                      <tr key={row.billing}>
                        <td className="maintenance-label">{row.billing}</td>
                        <td className="maintenance-value">{row.hourlyRate}</td>
                        <td className="commercial-table__commitment">
                          {row.commitment}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section> */}

          <section className="py-5 bg-deep">
            <div className="section-container">
              <section className="text-center  faq-title">
                <h1>
                  {t('services.faq_title_prefix')}{' '}
                  <span className="title-accent">
                    {t('services.faq_title_accent')}
                  </span>
                </h1>
              </section>
            </div>
            <section className="accordion-container">
              <Accordion
                defaultActiveKey="0"
                flush
                data-bs-theme="dark"
                onSelect={(eventKey) =>
                  eventKey &&
                  trackMatomoEvent(
                    'Content',
                    'accordion_open',
                    `Services FAQ ${eventKey}`,
                  )
                }
              >
                <Accordion.Item eventKey="1">
                  <Accordion.Header>{t('services.faq.q1')}</Accordion.Header>
                  <Accordion.Body className="acoordion">
                    {t('services.faq.a1')}
                  </Accordion.Body>
                </Accordion.Item>
                <Accordion.Item eventKey="2">
                  <Accordion.Header>{t('services.faq.q2')}</Accordion.Header>
                  <Accordion.Body className="acoordion">
                    {t('services.faq.a2')}
                  </Accordion.Body>
                </Accordion.Item>
                <Accordion.Item eventKey="3">
                  <Accordion.Header>{t('services.faq.q3')}</Accordion.Header>
                  <Accordion.Body className="acoordion">
                    {t('services.faq.a3')}
                  </Accordion.Body>
                </Accordion.Item>
                <Accordion.Item eventKey="4">
                  <Accordion.Header>{t('services.faq.q4')}</Accordion.Header>
                  <Accordion.Body className="acoordion">
                    {t('services.faq.a4')}
                  </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="6">
                  <Accordion.Header>{t('services.faq.q6')}</Accordion.Header>
                  <Accordion.Body className="acoordion">
                    {t('services.faq.a6')}
                  </Accordion.Body>
                </Accordion.Item>
                <Accordion.Item eventKey="7">
                  <Accordion.Header>{t('services.faq.q7')}</Accordion.Header>
                  <Accordion.Body className="acoordion">
                    {t('services.faq.a7')}
                  </Accordion.Body>
                </Accordion.Item>
                <Accordion.Item eventKey="8">
                  <Accordion.Header>{t('services.faq.q8')}</Accordion.Header>
                  <Accordion.Body className="acoordion">
                    {t('services.faq.a8')}
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </section>
          </section>
        </div>
      </Container>
    </>
  );
};

export default Services;
